export const PLAN_CODES = {
  FREE: 'GRATIS',
  PROFESSIONAL: 'PROFESIONAL',
} as const;

export type KnownPlanCode = (typeof PLAN_CODES)[keyof typeof PLAN_CODES];

export interface PlanLimits {
  businessCategories: number;
  services: number | null;
}

export interface BusinessPlanUsage {
  businessCategoryCount: number;
  canAddBusinessCategory: boolean;
  canCreateService: boolean;
  limits: PlanLimits;
  planCode: string | null;
  planName: string | null;
  serviceCount: number;
}

export interface BusinessPlanUsageRow {
  business_category_count: number | null;
  can_add_business_category: boolean | null;
  can_create_service: boolean | null;
  max_business_categories: number | null | undefined;
  max_services: number | null | undefined;
  plan_code: string | null;
  plan_name: string | null;
  service_count: number | null;
}

export const PLAN_LIMITS: Record<KnownPlanCode, PlanLimits> = {
  [PLAN_CODES.FREE]: {
    businessCategories: 1,
    services: 3,
  },
  [PLAN_CODES.PROFESSIONAL]: {
    businessCategories: 10,
    services: null,
  },
};

export function normalizePlanCode(planCode: string | null | undefined): string | null {
  const normalized = planCode?.trim().toUpperCase();
  return normalized || null;
}

export function isProfessionalPlan(
  planCode: string | null | undefined
): boolean {
  return normalizePlanCode(planCode) === PLAN_CODES.PROFESSIONAL;
}

export function getPlanLimits(
  planCode: string | null | undefined,
  serverLimits?: Partial<PlanLimits>
): PlanLimits {
  const normalizedPlanCode = normalizePlanCode(planCode);
  const fallback = isProfessionalPlan(normalizedPlanCode)
    ? PLAN_LIMITS[PLAN_CODES.PROFESSIONAL]
    : PLAN_LIMITS[PLAN_CODES.FREE];

  const businessCategories =
    typeof serverLimits?.businessCategories === 'number' &&
    Number.isInteger(serverLimits.businessCategories) &&
    serverLimits.businessCategories > 0
      ? serverLimits.businessCategories
      : fallback.businessCategories;
  const services =
    serverLimits?.services === null
      ? null
      : typeof serverLimits?.services === 'number' &&
          Number.isInteger(serverLimits.services) &&
          serverLimits.services > 0
        ? serverLimits.services
        : fallback.services;

  return { businessCategories, services };
}

function normalizeCount(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

export function normalizeBusinessPlanUsage(
  row: BusinessPlanUsageRow
): BusinessPlanUsage {
  const planCode = normalizePlanCode(row.plan_code);
  const isKnownPlan =
    planCode === PLAN_CODES.FREE || planCode === PLAN_CODES.PROFESSIONAL;
  const businessCategoryCount = normalizeCount(row.business_category_count);
  const serviceCount = normalizeCount(row.service_count);
  const hasAuthoritativeServerLimits =
    typeof row.max_business_categories === 'number' &&
    Number.isInteger(row.max_business_categories) &&
    row.max_business_categories > 0 &&
    (row.max_services === null ||
      (typeof row.max_services === 'number' &&
        Number.isInteger(row.max_services) &&
        row.max_services > 0));
  const limits = getPlanLimits(planCode, {
    businessCategories: row.max_business_categories ?? undefined,
    services: row.max_services,
  });

  const computedCanAddBusinessCategory =
    businessCategoryCount < limits.businessCategories;
  const computedCanCreateService =
    limits.services === null || serviceCount < limits.services;

  return {
    businessCategoryCount,
    canAddBusinessCategory:
      hasAuthoritativeServerLimits &&
      typeof row.can_add_business_category === 'boolean'
        ? row.can_add_business_category
        : computedCanAddBusinessCategory,
    canCreateService:
      hasAuthoritativeServerLimits &&
      typeof row.can_create_service === 'boolean'
        ? row.can_create_service
        : computedCanCreateService,
    limits,
    planCode,
    planName: row.plan_name?.trim() || null,
    serviceCount,
  };
}

export function hasProfessionalPlanBenefits(
  usage: BusinessPlanUsage
): boolean {
  return (
    isProfessionalPlan(usage.planCode) ||
    usage.limits.businessCategories > PLAN_LIMITS[PLAN_CODES.FREE].businessCategories ||
    usage.limits.services === null ||
    (typeof usage.limits.services === 'number' &&
      usage.limits.services > PLAN_LIMITS[PLAN_CODES.FREE].services!)
  );
}

export function createFallbackBusinessPlanUsage({
  businessCategoryCount = 0,
  planCode = null,
  planName = null,
  serviceCount = 0,
}: {
  businessCategoryCount?: number;
  planCode?: string | null;
  planName?: string | null;
  serviceCount?: number;
} = {}): BusinessPlanUsage {
  return normalizeBusinessPlanUsage({
    business_category_count: businessCategoryCount,
    can_add_business_category: null,
    can_create_service: null,
    max_business_categories: null,
    max_services: undefined,
    plan_code: planCode,
    plan_name: planName,
    service_count: serviceCount,
  });
}
